export const GOAL_REGISTRY = {
  revenue: { label: 'Revenue', unit: 'currency', scope: 'individual', source: 'deals', calc: 'sum_revenue', auto: true, description: 'Sales value from won deals' },
  pipeline: { label: 'Pipeline', unit: 'currency', scope: 'individual', source: 'deals', calc: 'sum_pipeline', auto: true, description: 'Open sales opportunity value' },
  deals_won: { label: 'Deals Won', unit: 'count', scope: 'individual', source: 'deals', calc: 'count_won', auto: true, description: 'Number of closed deals' },
  new_customers: { label: 'New Customers', unit: 'count', scope: 'individual', source: 'clients', calc: 'count_new_customers', auto: true, description: 'New customers acquired' },
  activities: { label: 'Activities', unit: 'count', scope: 'individual', source: 'activities', calc: 'count', auto: true, description: 'Calls, emails or meetings completed', subTypes: ['calls','emails','meetings'] },
  org_revenue: { label: 'Organization Revenue', unit: 'currency', scope: 'organization', source: 'deals', calc: 'sum_revenue', auto: true, description: 'Company-wide closed-won deal amount' },
  breakthrough: { label: 'Breakthrough', unit: 'currency', scope: 'organization', source: 'deals', calc: 'sum_revenue', auto: true, description: 'Breakthrough target — sum of closed-won deal amounts in period' },
  profitable: { label: 'Profitable', unit: 'currency', scope: 'organization', source: 'deals', calc: 'sum_revenue', auto: true, description: 'Profitable target — sum of closed-won deal amounts in period' },
  headcount: { label: 'Headcount', unit: 'count', scope: 'organization', source: 'users', calc: 'count', auto: true, description: 'Team size target' },
  win_rate: { label: 'Win Rate', unit: 'percentage', scope: 'organization', source: 'deals', calc: 'won/total', auto: true, description: 'Won deals ÷ total deals' },
};

export let LEGACY_MAP = {
  calls: 'activities', emails: 'activities', meetings: 'activities',
  opportunities: 'deals_won', conversion_rate: null, csat: null, gross_profit: 'revenue', pipeline_value: 'pipeline', custom: 'revenue', yoy_growth: 'revenue'
};

let _goalTypesVersion = null;
let _goalTypesPromise = null;

/** Prefer server registry when available; keep local constants as fallback. */
export async function ensureGoalTypesLoaded({ force = false } = {}) {
  if (_goalTypesPromise && !force) return _goalTypesPromise;
  _goalTypesPromise = (async () => {
    try {
      const { ApiClient } = await import('../services/ApiClient.js');
      const data = await ApiClient.get('/api/v1/config/goal-types');
      if (!data?.types?.length) return GOAL_REGISTRY;
      for (const t of data.types) {
        if (!t?.id) continue;
        GOAL_REGISTRY[t.id] = {
          label: t.label,
          unit: t.unit,
          scope: t.scope,
          source: t.source,
          calc: t.calc,
          auto: t.auto !== false,
          description: t.description || '',
          ...(t.subTypes ? { subTypes: t.subTypes } : {}),
        };
      }
      if (data.legacy_map && typeof data.legacy_map === 'object') {
        LEGACY_MAP = { ...LEGACY_MAP, ...data.legacy_map };
      }
      _goalTypesVersion = data.version || null;
      return GOAL_REGISTRY;
    } catch (err) {
      console.warn('[GoalRegistry] /config/goal-types unavailable; using local registry', err);
      return GOAL_REGISTRY;
    } finally {
      _goalTypesPromise = null;
    }
  })();
  return _goalTypesPromise;
}

export function getGoalTypesVersion() {
  return _goalTypesVersion;
}

export const GOAL_PERIODS = [
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'yearly', label: 'Yearly' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'custom', label: 'Custom range' }
];

export const GOAL_DB_STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'paused', label: 'Paused' },
  { value: 'completed', label: 'Completed' }
];

export const CURRENCY_OPTIONS = [
  { value: 'INR', label: 'INR (₹)', symbol: '₹' },
  { value: 'USD', label: 'USD ($)', symbol: '$' },
  { value: 'EUR', label: 'EUR (€)', symbol: '€' },
  { value: 'GBP', label: 'GBP (£)', symbol: '£' }
];

export function canonicalType(raw) {
  if (!raw) return 'revenue';
  const k = raw.toLowerCase();
  if (GOAL_REGISTRY[k]) return k;
  const m = LEGACY_MAP[k];
  return m && GOAL_REGISTRY[m] ? m : 'revenue';
}
export function unitFor(type) { return GOAL_REGISTRY[canonicalType(type)]?.unit || 'count'; }
export function scopeFor(type) { return GOAL_REGISTRY[canonicalType(type)]?.scope || 'individual'; }
export const GOAL_TYPE_ORDER = Object.keys(GOAL_REGISTRY);
export const GOAL_TYPES = Object.fromEntries(Object.entries(GOAL_REGISTRY).map(([k,v])=> [k, {label:v.label, unitType:v.unit, category:v.scope==='organization'?'Organization':'Sales', scope:v.scope}]));
export const ORG_ONLY_TYPES = new Set(['org_revenue','breakthrough','profitable']);
